import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@cbrebuild/blocks';
import moment from 'moment';
import { fetchUserDealRefreshHistory, refreshUserDealData } from '../../services/admin-service';
import userEventService from '../../services/user-event-service';
import LoadMore from '../../nucleus/load-more/load-more';
import './admin-user-deal-refresh.scss';

const AdminUserDealRefresh = ({ toast }) => {
  // State for refresh form
  const [targetEmail, setTargetEmail] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  
  // State for history with pagination (no search)
  const [userRefreshHistory, setUserRefreshHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Ref to track if component is mounted
  const [isMounted, setIsMounted] = useState(true);
  
  const pageSize = 25;
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);
  
  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Format cooldown time display
  const formatCooldownTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get total deal count from JSON structure
  const getTotalDeals = (dealCounts) => {
    if (!dealCounts || typeof dealCounts !== 'object') return 0;
    return (dealCounts.prospect || 0) + (dealCounts.executing || 0) + (dealCounts.closed || 0);
  };

  // Format deal counts display
  const formatDealCounts = (dealCounts) => {
    if (!dealCounts || typeof dealCounts !== 'object') return '0';
    const total = getTotalDeals(dealCounts);
    return `${total} (Prospect:${dealCounts.prospect || 0}, Executing:${dealCounts.executing || 0}, Closed:${dealCounts.closed || 0})`;
  };

  // Handle cooldown countdown
  useEffect(() => {
    let timer;
    if (cooldownRemaining > 0 && isMounted) {
      timer = setTimeout(() => {
        if (isMounted) {
          setCooldownRemaining(prev => prev - 1);
        }
      }, 1000);
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [cooldownRemaining, isMounted]);

  // Load refresh history with pagination
  const loadRefreshHistory = useCallback(async (page = 1, append = false) => {
    if ((historyLoading || loadingMore) && !isMounted) return;
    
    if (append) {
      setLoadingMore(true);
    } else {
      setHistoryLoading(true);
    }
    
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      
      // If targetEmail has value, use it for filtering (legacy behavior)
      if (targetEmail.trim()) {
        params.email = targetEmail.trim();
      }
      
      const response = await fetchUserDealRefreshHistory(params);
      
      if (isMounted) {
        if (append) {
          setUserRefreshHistory(prev => [...prev, ...(response.results || [])]);
        } else {
          setUserRefreshHistory(response.results || []);
        }
        setTotalCount(response.count || 0);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to load user refresh history');
      if (isMounted) {
        const errorMessage = 'Failed to load refresh history. Please try again.';
        toast(String(errorMessage));
        if (!append) {
          setUserRefreshHistory([]);
          setTotalCount(0);
        }
      }
    } finally {
      if (isMounted) {
        if (append) {
          setLoadingMore(false);
        } else {
          setHistoryLoading(false);
        }
      }
    }
  }, [targetEmail, historyLoading, loadingMore, toast, isMounted]);

  // Handle load more
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    loadRefreshHistory(nextPage, true);
  };

  // Handle refresh form submission
  const handleRefresh = useCallback(async (e) => {
    e.preventDefault();
    
    setEmailError('');
    
    const trimmedEmail = targetEmail.trim();
    if (!trimmedEmail) {
      setEmailError('Email address is required');
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setIsRefreshing(true);
    
    try {
      const response = await refreshUserDealData({ email: trimmedEmail });
      
      if (!isMounted) return;

      // Track successful deal refresh
      userEventService.trackEvent({
        eventCategory: 'Admin Capabilities',
        eventAction: 'admin_user_deal_refresh',
        eventLabel: 'admin_manage_deal',
      });
      
      const message = response?.message || 'Deal refresh initiated successfully';
      toast(String(message));
      
    } catch (error) {
      console.error('Refresh failed');
      
      if (!isMounted) return;
      
      if (error.status === 404) {
        const notFoundMessage = error.body?.error || 'User not found';
        toast(String(notFoundMessage));
      } else {
        const errorMessage = error.body?.error || error.message || 'Failed to refresh user deal data';
        toast(String(errorMessage));
      }
    } finally {
      if (isMounted) {
        setIsRefreshing(false);
      }
    }
  }, [targetEmail, toast, isMounted]);

  // Handle View History button click
  const handleViewHistory = () => {
    if (showHistory) {
      setShowHistory(false);
      setUserRefreshHistory([]);
      setCurrentPage(1);
      setTotalCount(0);
    } else {
      setShowHistory(true);
      loadRefreshHistory(1);
      
      // Track history view
      userEventService.trackEvent({
        eventCategory: 'Admin Capabilities',
        eventAction: 'admin_deal_refresh_history_viewed',
        eventLabel: 'admin_manage_deal',
      });
    }
  };

  // Format refresh status for display
  const getStatusDisplay = (status) => {
    const statusConfig = {
      pending: { text: 'Pending', className: 'pending' },
      in_progress: { text: 'In Progress', className: 'progress' },
      success: { text: 'Success', className: 'active' },
      failed: { text: 'Failed', className: 'inactive' },
    };
    
    return statusConfig[status] || { text: status, className: 'inactive' };
  };

  return (
    <>
    <div className="admin-user-deal-refresh-panel">
      <div className="admin-user-deal-refresh-header">
        <h3>User Deal Refresh</h3>
        <p>Manually refresh a user's deal data when they report missing or outdated deals.</p>
      </div>

      {/* Refresh Form */}
      <form onSubmit={handleRefresh} className="admin-user-deal-refresh-form">
        <div className="admin-user-deal-refresh-form-group">
          <label className="admin-user-deal-refresh-form-label">
            User Email Address *
          </label>
          <input
            type="email"
            className={`admin-user-deal-refresh-form-input ${emailError ? 'admin-user-deal-refresh-error' : ''}`}
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            placeholder="Enter user's email address (e.g., john.doe@cbre.com)"
            disabled={isRefreshing || cooldownRemaining > 0}
            required
          />
          {emailError && (
            <div className="admin-user-deal-refresh-form-error">
              {emailError}
            </div>
          )}
        </div>
        
        <div className="admin-user-deal-refresh-form-actions">
          <Button
            type="submit"
            className="admin-user-deal-refresh-submit-btn"
            disabled={isRefreshing || cooldownRemaining > 0 || !targetEmail.trim()}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Deals'}
          </Button>
          
          <Button
            type="button"
            className="admin-user-deal-refresh-history-btn"
            disabled={historyLoading}
            onClick={handleViewHistory}
          >
            {historyLoading ? 'Loading...' : showHistory ? 'Hide History' : 'View History'}
          </Button>
        </div>
        
        {cooldownRemaining > 0 && (
          <div className="admin-user-deal-refresh-cooldown">
            <Icon name="clock" size="sm" />
            <span>Cooldown: {formatCooldownTime(cooldownRemaining)}</span>
          </div>
        )}
      </form>

    </div>

    {/* User Deal Refresh History - Using shared styling pattern */}
    {showHistory && (
      <div className="admin-user-deal-refresh-history-card">
        <div className="admin-user-deal-refresh-history-header">
          <div className="admin-user-deal-refresh-history-header-content">
            <h4>User Deal Refresh History</h4>
            <p>
              {targetEmail.trim() 
                ? `Complete history of deal refresh operations for ${targetEmail}`
                : `All deal refresh operations (${totalCount} total records)`
              }
            </p>
          </div>
        </div>
          
        <div className="results-table">
          <div className="table-header">
            <div className="header-cell">ADMIN USER</div>
            <div className="header-cell">TARGET USER</div>
            <div className="header-cell">STATUS</div>
            <div className="header-cell">DEAL COUNTS</div>
            <div className="header-cell">LAST EXECUTED</div>
          </div>
          
          <div className="results-table-body">
            {historyLoading ? (
              <div className="table-row">
                <div className="cell" style={{gridColumn: '1 / 6', textAlign: 'center', padding: '32px'}}>
                  <Icon iconName="spinner" spin />
                  <span style={{marginLeft: '8px'}}>Loading history...</span>
                </div>
              </div>
            ) : userRefreshHistory && userRefreshHistory.length > 0 ? (
              userRefreshHistory.map((item, idx) => {
                const statusDisplay = getStatusDisplay(item.status);
                return (
                  <div className="table-row" key={item.id || idx}>
                    {/* ADMIN USER - First column */}
                    <div className="cell admin-user" data-label="Admin User">
                      <div className="member-info">
                        <div className="member-display-name">
                          {item.admin_user?.first_name} {item.admin_user?.last_name}
                        </div>
                        <div className="member-email">
                          {item.admin_user?.email || 'No email available'}
                        </div>
                      </div>
                    </div>

                    {/* TARGET USER - Second column */}
                    <div className="cell target-user" data-label="Target User">
                      <div className="member-info">
                        <div className="member-display-name">
                          {item.target_user?.first_name} {item.target_user?.last_name}
                        </div>
                        <div className="member-email">
                          {item.target_user?.email || 'No email available'}
                        </div>
                      </div>
                    </div>
                    
                    {/* STATUS - Third column */}
                    <div className="cell status" data-label="Status">
                      <span className={`status-badge ${statusDisplay.className}`}>
                        {statusDisplay.text}
                      </span>
                    </div>

                    {/* DEAL COUNTS - Fourth column */}
                    <div className="cell deal-counts" data-label="Deal Counts">
                      {item.previous_deal_count !== null && item.latest_deal_count !== null ? (
                        <div className="admin-user-deal-refresh-counts-info">
                          <div className="admin-user-deal-refresh-count-row">
                            <span className="admin-user-deal-refresh-count-label">Previous:</span>
                            <span className="admin-user-deal-refresh-count-value">{formatDealCounts(item.previous_deal_count)}</span>
                          </div>
                          <div className="admin-user-deal-refresh-count-row">
                            <span className="admin-user-deal-refresh-count-label">Latest:</span>
                            <span className="admin-user-deal-refresh-count-value">{formatDealCounts(item.latest_deal_count)}</span>
                          </div>
                          {getTotalDeals(item.latest_deal_count) !== getTotalDeals(item.previous_deal_count) && (
                            <div className="count-change">
                              {getTotalDeals(item.latest_deal_count) > getTotalDeals(item.previous_deal_count) ? (
                                <span className="deals-added">
                                  +{getTotalDeals(item.latest_deal_count) - getTotalDeals(item.previous_deal_count)} deals added
                                </span>
                              ) : (
                                <span className="deals-removed">
                                  {getTotalDeals(item.latest_deal_count) - getTotalDeals(item.previous_deal_count)} deals removed
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="no-counts">Processing...</span>
                      )}
                    </div>
                    
                    {/* LAST EXECUTED - Fifth column */}
                    <div className="cell executed-at" data-label="Last Executed">
                      <div className="executed-time">
                        {item.executed_at ? moment(item.executed_at).format('MMM DD, YYYY hh:mm A') : 'N/A'}
                      </div>
                      {item.error_message && (
                        <div className="admin-user-deal-refresh-error-note">
                          <Icon iconName="alert-triangle" size="sm" />
                          <span>Error: {item.error_message}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="table-row">
                <div className="cell" style={{gridColumn: '1 / 6', textAlign: 'center', padding: '32px', color: '#6b7280'}}>
                  {targetEmail.trim() ? 'No refresh history found for this user.' : 'No refresh history records found.'}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Load More button - reusing pool accounts LoadMore component */}
        <LoadMore
          isLoading={loadingMore}
          hasNextPage={userRefreshHistory.length < totalCount}
          handleLoadMore={handleLoadMore}
        />
      </div>
    )}
    </>
  );
};

AdminUserDealRefresh.propTypes = {
  toast: PropTypes.func.isRequired,
};

export default AdminUserDealRefresh;
